import { useInfiniteQuery } from "@tanstack/react-query";
import type { AuditLogListResponse, AuditLogQuery } from "@ticketnjoy/types";

import { apiClient } from "@/lib/api-client";

/**
 * Filtre objesi cache key — `from`/`to`/`actorMemberId`/`entityType`/`action`
 * değişince sayfa sıfırlanır, infinite query baştan başlar.
 */
export const auditQueryKey = (filters: Omit<AuditLogQuery, "page" | "pageSize">) =>
  ["audit", "list", filters] as const;

function toQueryString(query: AuditLogQuery): string {
  const params = new URLSearchParams();
  if (query.from) params.set("from", query.from);
  if (query.to) params.set("to", query.to);
  if (query.actorMemberId) params.set("actorMemberId", query.actorMemberId);
  if (query.entityType) params.set("entityType", query.entityType);
  if (query.action) params.set("action", query.action);
  if (query.page && query.page > 1) params.set("page", String(query.page));
  if (query.pageSize) params.set("pageSize", String(query.pageSize));
  const s = params.toString();
  return s ? `?${s}` : "";
}

/**
 * "Daha fazla yükle" pattern'ı (Orders ile aynı). 25 satır/sayfa default;
 * filtre değişince queryKey değişir → React Query yeni request'e başlar.
 */
export function useAuditInfiniteQuery(
  filters: Omit<AuditLogQuery, "page" | "pageSize"> = {},
) {
  const pageSize = 25;
  return useInfiniteQuery<AuditLogListResponse>({
    queryKey: auditQueryKey(filters),
    queryFn: ({ pageParam = 1 }) =>
      apiClient<AuditLogListResponse>(
        `/audit${toQueryString({ ...filters, page: pageParam as number, pageSize })}`,
      ),
    initialPageParam: 1,
    getNextPageParam: (last) => (last.hasMore ? last.page + 1 : undefined),
  });
}
